import moment from 'moment'
import React, { useState } from 'react'
import styled from 'styled-components'

import Calendar from 'components/Calendar'

import Events from 'pages/DeviceAggregator/FaceplateMessages/Events'

import { useStoreContext } from 'hooks/StoreContext'

import Api from 'services/api'

import { color } from 'styles/colors'
import { PanelHeaderSecondaryButton, screenSize } from 'styles/shared'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import RunPastCSVModal from 'pages/DeviceAggregator/FaceplateMessages/RunPastCSVModal'
import { useServerErrorContext } from 'hooks/ServerErrorContext'

import blobToFileDownload from 'helpers/blobToFileDownload'

type Props = {
  deviceId: string
  timeZone: string
}

const MIN_DATE = moment().subtract(30, 'days')

const FaceplateMessages = ({ deviceId, timeZone }: Props) => {
  const { faceplateMessages, areFaceplateMessagesLoaded } = useStoreContext()
  const [date, setDate] = useState(moment().startOf('days'))
  const [startAndEndTime, setStartAndEndTime] = useState({ start: date.clone(), end: date.clone().endOf('day') })
  const [isRunCSVOpen, setIsRunCSVOpen] = useState(false)
  const [jobId, setJobId] = useState('')
  const { setServerError } = useServerErrorContext()

  const onDateChange = (newDate) => {
    setDate(moment(newDate, 'YYYY-MM-DD'))
    setStartAndEndTime({ start: moment(newDate, 'YYYY-MM-DD'), end: moment(newDate, 'YYYY-MM-DD').endOf('day') })
  }

  const generate30DaysCSV = (deviceId) => {
    Api.DeviceAggregator.triggerPastFaceplateMessagesCSV(deviceId)
      .then((res) => {
        setJobId(res.jobId)
        setIsRunCSVOpen(true)
      })
      .catch(() => {
        setServerError('Error triggering 30 day CSV Report')
      })
  }

  const onDownloadCsvClick = () => {
    Api.DeviceAggregator.getFaceplateMessagesCSV(deviceId, {
      startTime: startAndEndTime.start.format('YYYY-MM-DD HH:mm:ss'),
      endTime: startAndEndTime.end.format('YYYY-MM-DD HH:mm:ss')
    }).then((res) => {
      blobToFileDownload(res, `FaceplateMessages_${startAndEndTime.start.format('YYYY-MM-DD')}.csv`)
    })
  }

  return (
    <div>
      <S.DateAndTimePickerContainer>
        <S.Date>
          <span>{moment(date).format('MMMM D, YYYY')} </span>
          <Calendar
            date={date.format('YYYY-MM-DD')}
            minDate={MIN_DATE}
            variant="textOnly"
            onDateChangeCallback={onDateChange}
          />
        </S.Date>

        {
          areFaceplateMessagesLoaded && faceplateMessages !== null && faceplateMessages.length !== 0 &&
          <S.DownloadLink
            target="_blank"
            aria-label="csv download link"
            onClick={onDownloadCsvClick}
          >
            Download CSV
          </S.DownloadLink>
        }
        <Button decoration="secondary" styles={PanelHeaderSecondaryButton} text="Generate 30 Days CSV" onPress={() => generate30DaysCSV(deviceId)} />
        <RunPastCSVModal
          deviceId={deviceId}
          isOpen={isRunCSVOpen}
          jobId={jobId}
          onClose={() => setIsRunCSVOpen(false)}
        />
      </S.DateAndTimePickerContainer>
      <Events deviceId={deviceId}
        startTime={startAndEndTime.start.format('YYYY-MM-DD HH:mm:ss')}
        endTime={startAndEndTime.end.format('YYYY-MM-DD HH:mm:ss')}
        timeZone={timeZone}
      />
    </div>
  )
}

export default FaceplateMessages

const S = {
  DateAndTimePickerContainer: styled.div`
    display: flex;
    align-items: center;
    padding: 20px 0px 10px 0px;
    gap: 20px;
    @media ${screenSize.small} {
      flex-direction: column;
      align-items: start;
    }
  `,
  Date: styled.div`
    display: flex;
    white-space: nowrap;
    font-size: 20px;
    font-weight: bold;
    text-transform: uppercase;
    color: ${color('rthChartDate')};

    @media ${screenSize.small} {
      align-self: start;
      padding-bottom: 10px;
    },

  `,
  DownloadLink: styled.a`
      margin: 5px 15px;
      display: inline-block;
  `
}
